import React, { useEffect, useState } from 'react';
import useAppNavigation from '../hooks/useAppNavigation';

const GOLD = '#C9A84C';

const ICONS = {
  typo_correction: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
  ),
  recommendation: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
    </svg>
  ),
  help_tip: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  playlist_idea: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" /><path d="M15 3h6v6" /><path d="M10 14L21 3" />
    </svg>
  ),
  follow_up: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  ),
};

/**
 * Subtle, non-intrusive suggestion banner.
 * Slides up from the bottom (above MiniPlayer), auto-dismisses after 12s.
 */
export default function ProactiveAssist({ suggestion, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const nav = useAppNavigation();

  useEffect(() => {
    if (suggestion) {
      setExiting(false);
      const showTimer = setTimeout(() => setVisible(true), 100);
      const autoHide = setTimeout(() => dismiss(), 12_000);
      return () => { clearTimeout(showTimer); clearTimeout(autoHide); };
    } else {
      setVisible(false);
    }
  }, [suggestion]);

  const dismiss = () => {
    setExiting(true);
    setTimeout(() => {
      setVisible(false);
      setExiting(false);
      onDismiss?.();
    }, 300);
  };

  const handleAction = () => {
    if (!suggestion?.action) { dismiss(); return; }
    const { type, payload } = suggestion.action;
    if (type === 'search' && payload?.query) {
      nav.goSearch?.(payload.query) || window.location.assign(`/app/search?q=${encodeURIComponent(payload.query)}`);
    } else if (type === 'navigate' && payload?.page) {
      if (payload.page === 'genres') nav.goGenres();
      else nav.goHome();
    } else if (type === 'open_valet') {
      document.querySelector('[data-valet-fab]')?.click();
    }
    dismiss();
  };

  if (!suggestion || !visible) return null;

  const icon = ICONS[suggestion.type] || ICONS.help_tip;

  return (
    <div
      className={`fixed left-4 right-4 z-[80] transition-all duration-300 ease-out ${
        exiting ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 200px)' }}
    >
      <div
        className="max-w-lg mx-auto rounded-2xl px-4 py-3 flex items-start gap-3 shadow-2xl border border-white/10"
        style={{ background: 'rgba(25, 25, 30, 0.95)', backdropFilter: 'blur(20px)' }}
      >
        <div className="mt-0.5 flex-shrink-0" style={{ color: GOLD }}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white/90 leading-snug">{suggestion.message}</p>
          {suggestion.action && (
            <button
              onClick={handleAction}
              className="mt-2 text-xs font-medium px-3 py-1 rounded-full transition-colors"
              style={{ color: GOLD, background: 'rgba(201, 168, 76, 0.12)', border: '1px solid rgba(201, 168, 76, 0.25)' }}
            >
              {suggestion.action.type === 'search' ? 'Try this search'
                : suggestion.action.type === 'create_playlist' ? 'Build playlist'
                : suggestion.action.type === 'open_valet' ? 'Ask Ella'
                : suggestion.action.type === 'navigate' ? 'Go there'
                : 'Show me'}
            </button>
          )}
        </div>
        <button
          onClick={dismiss}
          className="flex-shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded-full text-white/40 hover:text-white/70 hover:bg-white/10 transition"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18" /><path d="M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
